import { useState, useEffect, useRef, useCallback } from 'react';
import { formatDateLong } from '../../../utils/formatters';
import SearchSelect from '../../../components/SearchSelect';
import { useAuth } from '../../../contexts/AuthContext';
import { useConfig } from '../../../contexts/ConfigContext';

interface VoucherResult {
  code: string;
  voucher_value: number;
  discount_type: string;
  percentage_value?: number;
  max_discount_amount?: number;
  [key: string]: unknown;
}

/**
 * Create Voucher Modal — adapted for Sentinel (uses territories, direct insert)
 */
function MobileCreateVoucherModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { supabase, user, isSuperAdmin } = useAuth();
  const { formatCurrency, countryCode } = useConfig();

  const [territories, setTerritories] = useState<{ id: string; name: string; short_code: string | null }[]>([]);
  const [loadingTerritories, setLoadingTerritories] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ voucher: VoucherResult } | null>(null);
  const [copied, setCopied] = useState(false);

  const [selectedTerritory, setSelectedTerritory] = useState<{ id: string; name: string; short_code: string | null } | null>(null);
  const [discountType, setDiscountType] = useState('fixed');
  const [voucherValue, setVoucherValue] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [percentageValue, setPercentageValue] = useState('');
  const [maxDiscountAmount, setMaxDiscountAmount] = useState('');
  const [description, setDescription] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [unlimitedUsage, setUnlimitedUsage] = useState(true);
  const [usageLimit, setUsageLimit] = useState('');
  const [minServiceValue, setMinServiceValue] = useState('');
  const [singleUsePerCustomer, setSingleUsePerCustomer] = useState(false);
  const [newCustomersOnly, setNewCustomersOnly] = useState(false);

  // Code generator state
  const [codeSuffix, setCodeSuffix] = useState('');
  const [codeAvailable, setCodeAvailable] = useState<boolean | null>(null);
  const [checkingCode, setCheckingCode] = useState(false);
  const checkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // WhatsApp template
  const [shareTemplate, setShareTemplate] = useState<string | null>(null);

  const presetValues = [100, 250, 500];

  useEffect(() => {
    loadTerritories();
    loadShareTemplate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadShareTemplate() {
    try {
      const { data } = await supabase
        .from('whatsapp_templates')
        .select('message_template')
        .eq('country_code', countryCode || 'DK')
        .eq('template_key', 'voucher_share')
        .eq('is_active', true)
        .single();
      if (data) setShareTemplate(data.message_template);
    } catch (_err) {
      // Template not required
    }
  }

  function getFullCode() {
    const shortCode = selectedTerritory?.short_code;
    if (!shortCode) return null;
    return `V-${shortCode}-${codeSuffix}`;
  }

  function getRandomSuffix() {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    return chars.charAt(Math.floor(Math.random() * chars.length)) +
           chars.charAt(Math.floor(Math.random() * chars.length));
  }

  function getSuggestedSuffix() {
    const value = getSelectedValue();
    if (value <= 0) return '';
    const valuePart = discountType === 'percentage'
      ? `P${Math.round(value).toString().padStart(3, '0')}`
      : value.toString().padStart(4, '0');
    const randomPart = getRandomSuffix();
    const descLower = (description || '').toLowerCase().trim();
    let hint = '';
    if (descLower.includes('birthday')) hint = 'BD';
    else if (descLower.includes('wedding')) hint = 'WD';
    else if (descLower.includes('gift')) hint = 'GF';
    else if (descLower.includes('promo')) hint = 'PR';
    else if (descLower.includes('welcome')) hint = 'WC';
    else if (descLower.includes('test')) hint = 'TS';
    return valuePart + hint + randomPart;
  }

  const checkCodeAvailability = useCallback(async (code: string) => {
    if (!code || code.length < 8) { setCodeAvailable(null); return; }
    setCheckingCode(true);
    try {
      const { data } = await supabase.from('partner_vouchers').select('id').eq('code', code).maybeSingle();
      setCodeAvailable(!data);
    } catch { setCodeAvailable(null); }
    finally { setCheckingCode(false); }
  }, [supabase]);

  useEffect(() => {
    if (selectedTerritory?.short_code && getSelectedValue() > 0) {
      setCodeSuffix(getSuggestedSuffix());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTerritory, voucherValue, customValue, percentageValue, discountType, description]);

  useEffect(() => {
    const fullCode = getFullCode();
    if (!fullCode) return;
    if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current);
    checkTimeoutRef.current = setTimeout(() => checkCodeAvailability(fullCode), 500);
    return () => { if (checkTimeoutRef.current) clearTimeout(checkTimeoutRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeSuffix, selectedTerritory, checkCodeAvailability]);

  async function loadTerritories() {
    setLoadingTerritories(true);
    try {
      let query = supabase.from('territories').select('id, name, short_code').order('name');
      if (!isSuperAdmin && user?.id) {
        const { data: userTerr } = await supabase.from('user_territories').select('territory_id').eq('user_id', user.id);
        if (userTerr && userTerr.length > 0) {
          query = query.in('id', userTerr.map(ut => ut.territory_id));
        }
      }
      const { data } = await query;
      const list = data || [];
      setTerritories(list);
      if (list.length === 1) setSelectedTerritory(list[0] ?? null);
    } catch (_err) {
      setError('Failed to load territories');
    } finally {
      setLoadingTerritories(false);
    }
  }

  function getSelectedValue() {
    if (discountType === 'percentage') return parseFloat(percentageValue) || 0;
    if (voucherValue === 'custom') return parseFloat(customValue) || 0;
    return parseFloat(voucherValue) || 0;
  }

  async function handleCreate() {
    const errors: string[] = [];
    if (!selectedTerritory) errors.push('Select a territory');
    const value = getSelectedValue();
    if (discountType === 'fixed' && value <= 0) errors.push('Select a voucher value');
    if (discountType === 'percentage' && (value <= 0 || value > 100)) errors.push('Percentage must be between 1 and 100');

    const fullCode = getFullCode();
    if (selectedTerritory?.short_code && fullCode) {
      if (codeAvailable === false) errors.push('This voucher code is already taken');
      if (!codeSuffix || codeSuffix.length < 4) errors.push('Voucher code suffix must be at least 4 characters');
    }

    if (errors.length > 0) { setError(errors.join(' \u2022 ')); return; }

    setCreating(true);
    setError(null);

    try {
      const code = fullCode || `V-${Date.now().toString(36).toUpperCase()}`;
      const payload = {
        code,
        voucher_value: discountType === 'fixed' ? value : 0,
        discount_type: discountType,
        percentage_value: discountType === 'percentage' ? parseFloat(percentageValue) : null,
        max_discount_amount: discountType === 'percentage' && maxDiscountAmount ? parseFloat(maxDiscountAmount) : null,
        description: description.trim() || null,
        partner_id: user!.id,
        territory_id: selectedTerritory!.id,
        territory_restricted: true,
        min_service_value: minServiceValue ? parseFloat(minServiceValue) : null,
        single_use_per_customer: singleUsePerCustomer,
        new_customers_only: newCustomersOnly,
        expiry_date: expiryDate || null,
        usage_limit: unlimitedUsage ? null : (parseInt(usageLimit) || 1),
        payout_type: 'discount' as const,
        created_by: user!.id,
      };

      const { data, error: insertError } = await supabase.from('partner_vouchers').insert(payload).select().single();
      if (insertError) throw insertError;
      setResult({ voucher: data });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create voucher');
    } finally {
      setCreating(false);
    }
  }

  async function copyCode() {
    if (result?.voucher?.code) {
      await navigator.clipboard.writeText(result.voucher.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function buildShareMessage(): string {
    if (!result?.voucher) return '';
    const code = result.voucher.code;
    const isPercentage = result.voucher.discount_type === 'percentage';
    const val = isPercentage ? `${result.voucher.percentage_value}%` : formatCurrency(result.voucher.voucher_value);
    const senderName = user?.first_name || '';
    const expiryText = expiryDate
      ? formatDateLong(expiryDate)
      : '';

    let message = shareTemplate;
    if (message) {
      message = message.replace(/\{code\}/g, code).replace(/\{value\}/g, val).replace(/\{sender\}/g, senderName).replace(/\{expiry\}/g, expiryText);
      if (!expiryText) message = message.replace(/\n.*\{expiry_line\}.*\n?/g, '\n');
      message = message.replace(/\{expiry_line\}/g, expiryText ? `Valid until ${expiryText}` : '');
    } else {
      message = `Hi there,\n\nI'd love for you to experience a CleanMyBed professional mattress sanitisation \u2014 so I've arranged a *${val} voucher* for you.\n\nYour code: *${code}*\n\nSimply visit cleanmybed.com to book your service and mention this code when you do.\n\n` +
        (expiryText ? `Valid until ${expiryText}\n\n` : '') +
        `It really does make a difference to how you sleep. I hope you enjoy it.\n\n` +
        (senderName || '');
    }
    return message.trim();
  }

  function shareViaWhatsApp() {
    const message = buildShareMessage();
    if (!message) return;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  }

  function shareViaSms() {
    const message = buildShareMessage();
    if (!message) return;
    window.location.href = `sms:?body=${encodeURIComponent(message)}`;
  }

  // ── Success state ──
  if (result) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
          <div className="modal-header">
            <h2>Voucher Created</h2>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body" style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#x2705;</div>
            <div className="mb-xl">
              <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)', marginBottom: '0.5rem' }}>Voucher Code</div>
              <div style={{ fontSize: 'var(--text-4xl)', fontFamily: 'monospace', fontWeight: 700, padding: '0.75rem', background: 'var(--color-bg)', borderRadius: 'var(--radius)', letterSpacing: '0.05em' }}>
                {result.voucher?.code}
              </div>
            </div>
            <div className="text-lg fw-600 text-primary mb-lg">
              {result.voucher?.discount_type === 'percentage'
                ? `${result.voucher.percentage_value}% off${result.voucher.max_discount_amount ? ` (max ${formatCurrency(result.voucher.max_discount_amount)})` : ''}`
                : formatCurrency(result.voucher?.voucher_value)}
            </div>
            {description && <div style={{ fontSize: 'var(--text-base)', fontStyle: 'italic', marginBottom: '1rem', color: 'var(--color-text-light)' }}>&quot;{description}&quot;</div>}
            <div className="flex flex-col gap-2">
              <button className="btn btn-primary w-full" onClick={copyCode}>
                {copied ? '\u2713 Copied!' : 'Copy Code'}
              </button>
              <button className="btn btn-whatsapp w-full" onClick={shareViaWhatsApp}>
                💬 WhatsApp
              </button>
              <button className="btn btn-primary w-full" onClick={shareViaSms}>
                📲 SMS
              </button>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary w-full" onClick={onSuccess || onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Form ──
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
        <div className="modal-header">
          <h2>Create Voucher</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="alert alert-danger mb-lg">
              {error}
            </div>
          )}

          {/* Territory Selection */}
          {(territories.length > 1 || isSuperAdmin) && (
            <div className="form-group">
              <label className="form-label">Territory</label>
              {loadingTerritories ? (
                <div style={{ padding: '0.75rem', color: 'var(--color-text-light)' }}>Loading...</div>
              ) : (
                <SearchSelect
                  placeholder="Select territory..."
                  options={territories.map(t => ({ value: t.id, label: t.name }))}
                  value={selectedTerritory?.id || null}
                  onChange={v => {
                    const t = territories.find(t => t.id === v);
                    setSelectedTerritory(t || null);
                  }}
                />
              )}
            </div>
          )}

          {territories.length === 1 && !isSuperAdmin && selectedTerritory && (
            <div className="form-group">
              <label className="form-label">Territory</label>
              <div style={{ padding: '0.75rem', background: 'var(--color-bg)', borderRadius: 'var(--radius)', fontWeight: 500 }}>
                {selectedTerritory.name}
              </div>
            </div>
          )}

          {/* Discount Type */}
          <div className="form-group">
            <label className="form-label">Discount Type</label>
            <div style={{ display: 'flex', gap: 0 }}>
              <button type="button" onClick={() => setDiscountType('fixed')}
                style={{ flex: 1, padding: '0.625rem', fontWeight: 600, border: '2px solid', borderColor: discountType === 'fixed' ? 'var(--color-primary)' : 'var(--color-border)', borderRadius: '6px 0 0 6px', background: discountType === 'fixed' ? 'var(--color-primary)' : 'white', color: discountType === 'fixed' ? 'white' : 'var(--color-text)', cursor: 'pointer' }}>
                Fixed Amount
              </button>
              <button type="button" onClick={() => setDiscountType('percentage')}
                style={{ flex: 1, padding: '0.625rem', fontWeight: 600, border: '2px solid', borderLeft: 'none', borderColor: discountType === 'percentage' ? 'var(--color-primary)' : 'var(--color-border)', borderRadius: '0 6px 6px 0', background: discountType === 'percentage' ? 'var(--color-primary)' : 'white', color: discountType === 'percentage' ? 'white' : 'var(--color-text)', cursor: 'pointer' }}>
                Percentage
              </button>
            </div>
          </div>

          {/* Fixed Amount */}
          {discountType === 'fixed' && (
            <div className="form-group">
              <label className="form-label">Voucher Value</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem', marginBottom: '0.5rem' }}>
                {presetValues.map(val => (
                  <button key={val} type="button" onClick={() => { setVoucherValue(val.toString()); setCustomValue(''); }}
                    style={{ padding: '0.75rem', fontWeight: 600, border: '2px solid', borderColor: voucherValue === val.toString() ? 'var(--color-primary)' : 'var(--color-border)', borderRadius: 'var(--radius-md)', background: voucherValue === val.toString() ? 'var(--color-primary)' : 'white', color: voucherValue === val.toString() ? 'white' : 'var(--color-text)', cursor: 'pointer' }}>
                    {formatCurrency(val)}
                  </button>
                ))}
                <button type="button" onClick={() => setVoucherValue('custom')}
                  style={{ padding: '0.75rem', fontWeight: 600, border: '2px solid', borderColor: voucherValue === 'custom' ? 'var(--color-primary)' : 'var(--color-border)', borderRadius: 'var(--radius-md)', background: voucherValue === 'custom' ? 'var(--color-primary)' : 'white', color: voucherValue === 'custom' ? 'white' : 'var(--color-text)', cursor: 'pointer' }}>
                  Custom
                </button>
              </div>
              {voucherValue === 'custom' && (
                <input type="number" className="form-input" placeholder="Enter amount..." value={customValue}
                  onChange={e => setCustomValue(e.target.value)} min="1" step="1" />
              )}
            </div>
          )}

          {/* Percentage */}
          {discountType === 'percentage' && (
            <div className="form-group">
              <label className="form-label">Percentage Off</label>
              <div className="flex items-center gap-sm mb-sm">
                <input type="number" placeholder="e.g. 20" value={percentageValue}
                  onChange={e => setPercentageValue(e.target.value)} min="1" max="100" className="form-input" style={{ width: '100px', textAlign: 'center' }} />
                <span className="fw-600">%</span>
              </div>
              <label className="form-label text-xs">Maximum Discount (optional)</label>
              <input type="number" className="form-input" placeholder="No cap" value={maxDiscountAmount}
                onChange={e => setMaxDiscountAmount(e.target.value)} min="0" />
            </div>
          )}

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Description</label>
            <input type="text" className="form-input" placeholder="e.g. Birthday gift, Promo discount"
              value={description} onChange={e => setDescription(e.target.value)} maxLength={100} />
          </div>

          {/* Expiry */}
          <div className="form-group">
            <label className="form-label">Expiry Date (optional)</label>
            <input type="date" className="form-input" value={expiryDate}
              onChange={e => setExpiryDate(e.target.value)} min={new Date().toISOString().split('T')[0]} />
          </div>

          {/* Redemption Rules */}
          <div className="highlight-box mt-lg">
            <label className="form-label fw-600 mb-md">Redemption Rules</label>

            <div className="form-group mb-md">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-base)', cursor: 'pointer' }}>
                <input type="checkbox" checked={unlimitedUsage} onChange={e => { setUnlimitedUsage(e.target.checked); if (e.target.checked) setUsageLimit(''); }} />
                Unlimited redemptions
              </label>
              {!unlimitedUsage && (
                <div className="flex items-center gap-sm mt-sm">
                  <input type="number" value={usageLimit} onChange={e => setUsageLimit(e.target.value)} min="1" placeholder="e.g. 100"
                    className="form-input" style={{ width: '100px', textAlign: 'center' }} />
                  <span style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>total redemptions</span>
                </div>
              )}
            </div>

            <div className="form-group mb-md">
              <label className="form-label text-xs">Min Service Value (optional)</label>
              <input type="number" className="form-input" placeholder="No minimum" value={minServiceValue}
                onChange={e => setMinServiceValue(e.target.value)} min="0" />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-base)', cursor: 'pointer', marginBottom: '0.5rem' }}>
              <input type="checkbox" checked={singleUsePerCustomer} onChange={e => setSingleUsePerCustomer(e.target.checked)} />
              Single use per customer
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-base)', cursor: 'pointer' }}>
              <input type="checkbox" checked={newCustomersOnly} onChange={e => setNewCustomersOnly(e.target.checked)} />
              New customers only
            </label>
          </div>

          {/* Code Generator */}
          {selectedTerritory && getSelectedValue() > 0 && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--color-primary-bg)', borderRadius: 'var(--radius)', border: '2px solid var(--color-primary)' }}>
              <label className="form-label fw-600 text-primary">Voucher Code</label>
              {selectedTerritory.short_code ? (
                <>
                  <div className="flex items-center mb-sm">
                    <div style={{ padding: '0.625rem 0.5rem 0.625rem 0.75rem', background: 'var(--color-primary-border)', borderRadius: '6px 0 0 6px', fontFamily: 'monospace', fontWeight: 700, color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
                      V-{selectedTerritory.short_code}-
                    </div>
                    <input type="text" value={codeSuffix}
                      onChange={e => setCodeSuffix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                      placeholder="0250" maxLength={12}
                      style={{ flex: 1, padding: '0.625rem', border: '2px solid', borderLeft: 'none', borderColor: codeAvailable === false ? 'var(--color-error)' : codeAvailable === true ? 'var(--color-success)' : 'var(--color-border)', borderRadius: '0 6px 6px 0', fontFamily: 'monospace', fontWeight: 700, minWidth: 0 }} />
                  </div>
                  <div className="text-xs">
                    {checkingCode && <span className="text-muted">Checking...</span>}
                    {!checkingCode && codeAvailable === true && <span style={{ color: 'var(--color-success)', fontWeight: 500 }}>{'\u2713'} Available</span>}
                    {!checkingCode && codeAvailable === false && <span style={{ color: 'var(--color-error)', fontWeight: 500 }}>{'\u2717'} Already taken</span>}
                  </div>
                </>
              ) : (
                <div className="alert alert-warning">
                  Territory needs a short code. Code will be auto-generated.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer flex-col gap-md">
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-light)', textAlign: 'center' }}>
            By creating a voucher you agree to the Voucher Terms &amp; Conditions
          </div>
          <div className="flex gap-md w-full">
            <button className="btn btn-secondary flex-1" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreate} disabled={creating} style={{ flex: 2 }}>
              {creating ? 'Creating...' : 'Create Voucher'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MobileCreateVoucherModal;
